import { useTranslation } from 'react-i18next';
import { Flex, Form } from 'antd';
import { Frequency } from 'rrule';
import {
  CV50_FREQUENCY_OPTIONS,
  CV50_FREQUENCY_OPTIONS_LABELS,
  ONCE_FREQUENCY,
  WEEKDAYS_OPTIONS,
  WEEKDAY_LABEL_KEYS,
} from '../../../../types/MachineDetailsRobotSchedule.types';
import { useMachineDetailsRobotSchedule } from '../../../../context/MachineDetailsRobotScheduleContext';
import { useScheduleUpsertModalCV50 } from '../../../../context/ScheduleCreateModalContext';
import { FrequencyText, RepeatingTaskFormItem, DaySelectionText } from './RepeatingTask.styles';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';
import { KccCheckableTag } from 'lib/components/KccCheckableTag/KccCheckableTag';

export const RepeatingTaskCV50 = (): JSX.Element => {
  const { t } = useTranslation();

  const { isReadOnly } = useMachineDetailsRobotSchedule();
  const { form, handleFrequencyChange, handleWeekdaysChange } = useScheduleUpsertModalCV50();

  const frequency = Form.useWatch('scheduleFrequency', form);
  const weekdays = Form.useWatch('weekdays', form);

  return (
    <Flex vertical gap={kccThemeConstants.SPACING.LG} style={{ marginLeft: 36 }}>
      <Flex gap={kccThemeConstants.SPACING.SM}>
        <div style={{ flex: 1 }}>
          <FrequencyText>{t('createSchedule.frequency')}</FrequencyText>

          <RepeatingTaskFormItem
            name="scheduleFrequency"
            rules={[
              {
                validator: (_, value): Promise<void> =>
                  value === ONCE_FREQUENCY || value === Frequency.WEEKLY
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('createSchedule.frequencyRequired'))),
              },
            ]}
          >
            <Flex gap={kccThemeConstants.SPACING.XS}>
              {CV50_FREQUENCY_OPTIONS.map(tag => (
                <KccCheckableTag
                  key={tag}
                  checked={frequency === tag}
                  disabled={isReadOnly}
                  onChange={(checked): void => {
                    if (isReadOnly) {
                      return;
                    }

                    handleFrequencyChange(checked, tag);
                  }}
                >
                  {t(CV50_FREQUENCY_OPTIONS_LABELS[tag])}
                </KccCheckableTag>
              ))}
            </Flex>
          </RepeatingTaskFormItem>
        </div>
      </Flex>

      <Flex vertical gap={kccThemeConstants.SPACING.XXS}>
        <DaySelectionText>{t('createSchedule.daySelection')}</DaySelectionText>
        <RepeatingTaskFormItem
          name="weekdays"
          className="repeating-task-weekdays"
          rules={[{ required: true, message: t('createSchedule.daysOfWeekRequired') }]}
        >
          <Flex gap={kccThemeConstants.SPACING.XS} wrap>
            {WEEKDAYS_OPTIONS.map(tag => (
              <KccCheckableTag
                key={tag}
                checked={weekdays?.includes(tag) ?? false}
                disabled={isReadOnly}
                onChange={(checked): void => {
                  if (isReadOnly) {
                    return;
                  }

                  handleWeekdaysChange(checked, tag);
                }}
                style={{ textTransform: 'capitalize' }}
              >
                {t(WEEKDAY_LABEL_KEYS[tag])}
              </KccCheckableTag>
            ))}
          </Flex>
        </RepeatingTaskFormItem>
      </Flex>
    </Flex>
  );
};
